import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  if (b >= 1024)      return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
};

const fmtNum = (n) =>
  n == null ? "—" : Number(n).toLocaleString("ar-SA");

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
};

// ── Status systems ────────────────────────────────────────────────────────────
const storageSt = (pct) => {
  if (pct >= 90) return { label: "حرج",          color: "#dc2626", bg: "#fee2e2" };
  if (pct >= 80) return { label: "تحذير",         color: "#ea580c", bg: "#ffedd5" };
  if (pct >= 70) return { label: "مرتفع",         color: "#d97706", bg: "#fef3c7" };
  if (pct >= 50) return { label: "يحتاج مراقبة",  color: "#2563eb", bg: "#dbeafe" };
  return           { label: "طبيعي",              color: "#059669", bg: "#d1fae5" };
};

const budgetSt = (pct) => {
  if (pct >= 100)  return { label: "تجاوز الميزانية", color: "#dc2626", bg: "#fee2e2" };
  if (pct >= 87.5) return { label: "35+ ر.س",         color: "#ea580c", bg: "#ffedd5" };
  if (pct >= 75)   return { label: "30+ ر.س",          color: "#d97706", bg: "#fef3c7" };
  if (pct >= 50)   return { label: "20+ ر.س",          color: "#2563eb", bg: "#dbeafe" };
  return             { label: "ضمن الميزانية",          color: "#059669", bg: "#d1fae5" };
};

// ── Shared components (match admin-card / admin-kpi patterns exactly) ─────────
function ProgressBar({ pct, color, height = 8 }) {
  const c = Math.min(pct || 0, 100);
  return (
    <div style={{ background: "rgba(0,0,0,0.07)", borderRadius: 999, height, overflow: "hidden" }}>
      <div style={{
        height: "100%", width: `${c}%`, background: color, borderRadius: 999,
        transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
      }} />
    </div>
  );
}

function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: "inline-block", background: bg, color,
      border: `1px solid ${color}30`, borderRadius: 9999,
      padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
    }}>{label}</span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{
      background: "rgba(0,0,0,0.03)", borderRadius: "0.6rem",
      padding: "0.55rem 0.8rem",
    }}>
      <div style={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.38)", fontWeight: 700, marginBottom: "0.15rem" }}>{label}</div>
      <div style={{ fontSize: "0.88rem", fontWeight: 900, color: "rgba(0,0,0,0.72)" }}>{value}</div>
    </div>
  );
}

// ── Alert banner ──────────────────────────────────────────────────────────────
const ALERT_STYLES = {
  critical: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5", icon: "🔴" },
  danger:   { bg: "#ffedd5", color: "#c2410c", border: "#fdba74", icon: "🟠" },
  warn:     { bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "🟡" },
  info:     { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", icon: "ℹ️" },
};

function AlertBanner({ level, msg }) {
  const s = ALERT_STYLES[level] || ALERT_STYLES.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, borderRadius: "0.75rem",
      padding: "0.7rem 1rem", fontSize: "0.84rem", color: s.color,
      fontWeight: 700, display: "flex", alignItems: "center", gap: "0.5rem",
      marginBottom: "0.65rem",
    }}>
      <span>{s.icon}</span> {msg}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function DbUsagePanel({ token }) {
  const [stats,      setStats]      = useState(null);
  const [reports,    setReports]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch,  setLastFetch]  = useState(null);
  const [cooldown,   setCooldown]   = useState(false);
  const [coolLeft,   setCoolLeft]   = useState(0);
  const [tab,        setTab]        = useState("overview");

  const hdrs = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (force = false) => {
    try {
      const [sRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/db-stats${force ? "?force=true" : ""}`, { headers: hdrs }),
        axios.get(`${API}/admin/db-daily-reports`, { headers: hdrs }),
      ]);
      setStats(sRes.data);
      setReports(rRes.data || []);
      setLastFetch(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر تحميل إحصاءات قاعدة البيانات");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleRefresh = () => {
    if (cooldown || refreshing) return;
    setRefreshing(true);
    load(true);
    // 10-minute cooldown
    setCooldown(true);
    let left = 600;
    setCoolLeft(left);
    const t = setInterval(() => {
      left -= 1;
      setCoolLeft(left);
      if (left <= 0) { clearInterval(t); setCooldown(false); setCoolLeft(0); }
    }, 1000);
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const storage     = stats?.storage || {};
  const cost        = stats?.cost    || {};
  const collections = stats?.collections || [];
  const sSt         = storageSt(storage.pct_used || 0);
  const bSt         = budgetSt(cost.budget_pct || 0);

  const alerts = [];
  const sar = cost.currency_sar || 0;
  if (sar >= 40)                    alerts.push({ level: "critical", msg: `بلغ استهلاك MongoDB الميزانية الشهرية المحددة: 40 ر.س` });
  else if (sar >= 35)               alerts.push({ level: "danger",   msg: `تبقّى أقل من 5 ر.س قبل ميزانية MongoDB. راجع Atlas Dashboard.` });
  else if (sar >= 30)               alerts.push({ level: "warn",     msg: `اقترب استهلاك MongoDB من الميزانية الشهرية (${sar.toFixed(2)} ر.س من 40).` });
  else if (sar >= 20)               alerts.push({ level: "info",     msg: `وصل استهلاك MongoDB إلى ${sar.toFixed(2)} ر.س هذا الشهر.` });
  if (cost.will_exceed_budget)      alerts.push({ level: "warn",     msg: "بناءً على معدل الاستخدام الحالي، قد تتجاوز التكلفة 40 ر.س قبل نهاية الشهر." });

  const panelTabs = [
    { key: "overview",     label: "نظرة عامة" },
    { key: "collections",  label: "Collections" },
    { key: "reports",      label: `التقارير اليومية${reports.length > 0 ? ` (${reports.length})` : ""}` },
  ];

  const btnBase = {
    fontFamily: "Cairo, sans-serif", fontWeight: 700, cursor: "pointer",
    border: "none", transition: "all 0.18s",
  };

  if (loading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "rgba(0,0,0,0.3)", fontFamily: "Cairo, sans-serif" }}>
        ⏳ جاري تحميل إحصاءات قاعدة البيانات...
      </div>
    );
  }

  return (
    <div style={{ padding: "1.5rem", fontFamily: "Cairo, sans-serif", direction: "rtl", maxWidth: 1100, margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.1rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h2 style={{ fontSize: "1.2rem", fontWeight: 900, color: "rgba(0,0,0,0.8)", margin: "0 0 0.2rem" }}>
            🗄️ مراقبة قاعدة البيانات
          </h2>
          <p style={{ fontSize: "0.77rem", color: "rgba(0,0,0,0.38)", margin: 0 }}>
            MongoDB Atlas Flex · {storage.limit_gb || 5} GB
            {lastFetch && <> · آخر تحديث: {lastFetch.toLocaleTimeString("ar-SA")}</>}
            {stats?.stale && <span style={{ color: "#d97706" }}> · بيانات قديمة</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <a
            href="https://cloud.mongodb.com"
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: "0.78rem", color: "#2563eb", textDecoration: "none", fontWeight: 700 }}
          >
            Atlas Dashboard ↗
          </a>
          <button
            onClick={handleRefresh}
            disabled={refreshing || cooldown}
            style={{
              ...btnBase,
              background: refreshing || cooldown ? "rgba(0,0,0,0.06)" : "#5B0E14",
              color:       refreshing || cooldown ? "rgba(0,0,0,0.35)" : "#F1E194",
              borderRadius: "0.65rem", padding: "0.45rem 1rem", fontSize: "0.82rem",
              cursor: refreshing || cooldown ? "not-allowed" : "pointer",
            }}
          >
            {refreshing
              ? "⏳ يحدث..."
              : cooldown
              ? `↻ ${Math.floor(coolLeft / 60)}:${String(coolLeft % 60).padStart(2, "0")}`
              : "↻ تحديث"}
          </button>
        </div>
      </div>

      {/* ── Alerts ── */}
      {alerts.map((a, i) => <AlertBanner key={i} {...a} />)}

      {/* ── Inner tabs ── */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1.2rem", flexWrap: "wrap" }}>
        {panelTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              ...btnBase,
              background: tab === t.key ? "#5B0E14" : "transparent",
              color:       tab === t.key ? "#F1E194" : "rgba(0,0,0,0.5)",
              border:      tab === t.key ? "none" : "1px solid rgba(0,0,0,0.12)",
              borderRadius: "0.5rem", padding: "0.32rem 0.85rem", fontSize: "0.81rem",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: OVERVIEW
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>

          {/* Storage card */}
          <div className="admin-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.9rem", flexWrap: "wrap", gap: "0.5rem" }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: "0.93rem", color: "rgba(0,0,0,0.76)" }}>💾 استهلاك التخزين</div>
                <div style={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.38)", marginTop: "0.15rem" }}>مصدر القياس: dbStats — دقيق</div>
              </div>
              <Badge {...sSt} />
            </div>

            {/* Big number */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "0.45rem" }}>
              <span style={{ fontSize: "2.1rem", fontWeight: 900, color: sSt.color, lineHeight: 1 }}>
                {(storage.total_gb || 0).toFixed(3)}
                <span style={{ fontSize: "1rem", fontWeight: 700, color: "rgba(0,0,0,0.38)", marginRight: "0.25rem" }}>GB</span>
              </span>
              <span style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.4)", fontWeight: 700 }}>
                من {storage.limit_gb || 5} GB ({(storage.pct_used || 0).toFixed(1)}%)
              </span>
            </div>
            <ProgressBar pct={storage.pct_used} color={sSt.color} height={13} />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.55rem", marginTop: "1rem" }}>
              <InfoRow label="حجم البيانات"  value={fmtBytes(storage.data_bytes)} />
              <InfoRow label="حجم التخزين"   value={fmtBytes(storage.storage_bytes)} />
              <InfoRow label="حجم الفهارس"   value={fmtBytes(storage.index_bytes)} />
              <InfoRow label="آخر تحديث"     value={fmtTime(stats?.last_updated)} />
            </div>
          </div>

          {/* Cost card */}
          <div className="admin-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.9rem", flexWrap: "wrap", gap: "0.5rem" }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: "0.93rem", color: "rgba(0,0,0,0.76)" }}>💰 تكلفة MongoDB هذا الشهر</div>
                <div style={{ fontSize: "0.72rem", color: "#d97706", fontWeight: 700, marginTop: "0.15rem" }}>
                  ⚠️ تقديرية — الفاتورة الفعلية في{" "}
                  <a href="https://cloud.mongodb.com" target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "underline dotted" }}>
                    Atlas Dashboard
                  </a>
                </div>
              </div>
              <Badge {...bSt} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div>
                <div style={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.4)", fontWeight: 700, marginBottom: "0.25rem" }}>تقدير حتى الآن</div>
                <div style={{ fontSize: "1.9rem", fontWeight: 900, color: "#5B0E14", lineHeight: 1 }}>
                  {(cost.currency_sar || 0).toFixed(2)}
                  <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "rgba(0,0,0,0.38)", marginRight: "0.2rem" }}>ر.س</span>
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.35)", marginTop: "0.2rem" }}>
                  ≈ ${(cost.currency_usd || 0).toFixed(2)} USD
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.4)", fontWeight: 700, marginBottom: "0.25rem" }}>التوقع لنهاية الشهر</div>
                <div style={{ fontSize: "1.9rem", fontWeight: 900, color: cost.will_exceed_budget ? "#dc2626" : "#059669", lineHeight: 1 }}>
                  {(cost.monthly_projected_sar || 0).toFixed(2)}
                  <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "rgba(0,0,0,0.38)", marginRight: "0.2rem" }}>ر.س</span>
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.35)", marginTop: "0.2rem" }}>
                  ≈ ${(cost.monthly_projected_usd || 0).toFixed(2)} USD
                </div>
              </div>
            </div>

            {/* Budget progress bar */}
            <div style={{ background: "rgba(0,0,0,0.03)", borderRadius: "0.75rem", padding: "0.8rem 1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.45rem" }}>
                <span style={{ fontSize: "0.79rem", fontWeight: 700, color: "rgba(0,0,0,0.58)" }}>
                  الميزانية الشهرية: {cost.budget_sar || 40} ر.س
                </span>
                <span style={{ fontSize: "0.79rem", fontWeight: 900, color: bSt.color }}>
                  {(cost.budget_pct || 0).toFixed(1)}%
                </span>
              </div>
              <ProgressBar pct={cost.budget_pct} color={bSt.color} height={10} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.4rem", fontSize: "0.7rem", color: "rgba(0,0,0,0.38)" }}>
                <span>متبقي: {(cost.remaining_sar || 0).toFixed(2)} ر.س</span>
                <span>1 USD = {cost.sar_per_usd || 3.75} ر.س</span>
              </div>
            </div>

            <div style={{ marginTop: "0.65rem", fontSize: "0.68rem", color: "rgba(0,0,0,0.3)", padding: "0.45rem 0.7rem", background: "rgba(0,0,0,0.02)", borderRadius: "0.5rem" }}>
              {cost.rate_basis}
            </div>
          </div>

          {/* Operational KPIs — from latest daily report */}
          {reports.length > 0 && (() => {
            const latest = reports[0];
            const kpis = [
              { icon: "👥", label: "المستخدمون",     val: latest.users_count,     color: "#2563eb" },
              { icon: "📂", label: "الفئات",          val: latest.categories_count, color: "#7c3aed" },
              { icon: "❓", label: "الأسئلة",         val: latest.questions_count,  color: "#059669" },
              { icon: "🎮", label: "المباريات",       val: latest.sessions_count,   color: "#d97706" },
              { icon: "💾", label: "المحفوظة",        val: latest.saves_count,      color: "#5B0E14" },
              { icon: "🚩", label: "البلاغات",        val: latest.reports_count,    color: "#dc2626" },
            ];
            return (
              <div className="admin-card">
                <div style={{ fontWeight: 900, fontSize: "0.93rem", color: "rgba(0,0,0,0.76)", marginBottom: "1rem" }}>
                  📊 إحصاءات تشغيلية
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.55rem" }}>
                  {kpis.map((k) => (
                    <div key={k.label} className="admin-kpi">
                      <div style={{ fontSize: "1.1rem", marginBottom: "0.12rem" }}>{k.icon}</div>
                      <div className="admin-kpi-value" style={{ color: k.color, fontSize: "1.5rem" }}>
                        {k.val == null ? "—" : fmtNum(k.val)}
                      </div>
                      <div className="admin-kpi-label">{k.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: "0.68rem", color: "rgba(0,0,0,0.28)", marginTop: "0.65rem", textAlign: "left" }}>
                  من تقرير {fmtTime(latest.generated_at)}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: COLLECTIONS
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === "collections" && (
        <div className="admin-card">
          <div style={{ fontWeight: 900, fontSize: "0.93rem", color: "rgba(0,0,0,0.76)", marginBottom: "1rem" }}>
            أكبر Collections — مرتبة حسب حجم التخزين
          </div>
          {collections.length === 0 ? (
            <p style={{ color: "rgba(0,0,0,0.28)", textAlign: "center", padding: "2rem 0" }}>لا توجد بيانات</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr>
                    {["Collection", "المستندات", "بيانات", "تخزين", "فهارس"].map((h) => (
                      <th key={h} style={{
                        padding: "0.55rem 0.8rem", textAlign: "right",
                        color: "rgba(0,0,0,0.38)", fontWeight: 700, fontSize: "0.7rem",
                        textTransform: "uppercase", letterSpacing: "0.04em",
                        borderBottom: "2px solid rgba(0,0,0,0.07)", whiteSpace: "nowrap",
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {collections.map((c, i) => (
                    <tr key={c.name} style={{ background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.018)" }}>
                      <td style={{ padding: "0.55rem 0.8rem", fontWeight: 700, color: "rgba(0,0,0,0.75)", borderBottom: "1px solid rgba(0,0,0,0.045)" }}>
                        {c.name}
                      </td>
                      <td style={{ padding: "0.55rem 0.8rem", borderBottom: "1px solid rgba(0,0,0,0.045)", color: "rgba(0,0,0,0.58)" }}>
                        {fmtNum(c.count)}
                      </td>
                      <td style={{ padding: "0.55rem 0.8rem", borderBottom: "1px solid rgba(0,0,0,0.045)", color: "rgba(0,0,0,0.55)" }}>
                        {fmtBytes(c.data_bytes)}
                      </td>
                      <td style={{ padding: "0.55rem 0.8rem", borderBottom: "1px solid rgba(0,0,0,0.045)", fontWeight: 700, color: "rgba(0,0,0,0.75)" }}>
                        {fmtBytes(c.storage_bytes)}
                      </td>
                      <td style={{ padding: "0.55rem 0.8rem", borderBottom: "1px solid rgba(0,0,0,0.045)", color: "rgba(0,0,0,0.45)" }}>
                        {fmtBytes(c.index_bytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: "0.68rem", color: "rgba(0,0,0,0.28)", marginTop: "0.75rem" }}>
            مصدر: collStats لكل Collection · آخر تحديث: {fmtTime(stats?.last_updated)}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: DAILY REPORTS
      ══════════════════════════════════════════════════════════════════════ */}
      {tab === "reports" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
          {reports.length === 0 ? (
            <div className="admin-card" style={{ textAlign: "center", padding: "2.5rem 1rem", color: "rgba(0,0,0,0.3)" }}>
              <div style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>📋</div>
              <p style={{ margin: 0 }}>
                لا توجد تقارير يومية بعد. سيتم إنشاء أول تقرير الساعة 11:50 مساءً بتوقيت الرياض.
              </p>
            </div>
          ) : (
            reports.map((r) => {
              const rSt = storageSt(r.storage_pct || 0);
              return (
                <div key={r.date_key} className="admin-card" style={{ padding: "0.8rem 1rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 900, fontSize: "0.88rem", color: "rgba(0,0,0,0.75)" }}>{r.date_key}</span>
                      <Badge label={r.status || "—"} color={rSt.color} bg={rSt.bg} />
                      {r.will_exceed_budget && <Badge label="⚠️ تجاوز متوقع" color="#d97706" bg="#fef3c7" />}
                    </div>
                    <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.48)" }}>
                        💾 {(r.storage_total_gb || 0).toFixed(3)} GB
                        <span style={{ color: rSt.color, marginRight: "0.3rem" }}>({(r.storage_pct || 0).toFixed(1)}%)</span>
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.48)" }}>
                        💰 {(r.cost_sar || 0).toFixed(2)} ر.س تقديرية
                      </span>
                      {!!r.daily_growth_bytes && r.daily_growth_bytes !== 0 && (
                        <span style={{ fontSize: "0.8rem", color: r.daily_growth_bytes > 0 ? "#dc2626" : "#059669", fontWeight: 700 }}>
                          {r.daily_growth_bytes > 0 ? "+" : ""}{fmtBytes(Math.abs(r.daily_growth_bytes))} يوم/
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Abnormal growth */}
                  {r.abnormal_collections?.length > 0 && (
                    <div style={{
                      marginTop: "0.55rem", padding: "0.45rem 0.75rem",
                      background: "#fef3c7", borderRadius: "0.5rem",
                      fontSize: "0.76rem", color: "#92400e", fontWeight: 700,
                    }}>
                      ⚠️ نمو غير معتاد:{" "}
                      {r.abnormal_collections.map((c) => `${c.name} (+${fmtBytes(c.growth_bytes)})`).join("، ")}
                    </div>
                  )}

                  {/* Mini stats row */}
                  <div style={{ display: "flex", gap: "1.25rem", marginTop: "0.55rem", flexWrap: "wrap" }}>
                    {[
                      ["👥", r.users_count],
                      ["❓", r.questions_count],
                      ["🎮", r.sessions_count],
                      ["💾", r.saves_count],
                    ].map(([icon, val], i) => val != null && (
                      <span key={i} style={{ fontSize: "0.76rem", color: "rgba(0,0,0,0.42)" }}>
                        {icon} {fmtNum(val)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
